import { AuthService, AuthenticationResult, LoginOptions } from '../services/AuthService';
import { AuthRepositoryFactory } from '../repositories/AuthRepositoryFactory';
import { Email } from '../value-objects/Email';
import { LoginAttempt } from '../value-objects/LoginAttempt';
import { User } from '../models/User';
import { AuthToken } from '../value-objects/AuthToken';
import { Session } from '../models/Session';

/**
 * 로그인 요청 DTO
 */
export interface LoginRequest {
  email: string;
  password: string;
  rememberMe?: boolean;
  deviceXCircle?: {
    userAgent: string;
    ipAddress: string;
    deviceName?: string;
  };
}

/**
 * 로그인 응답 DTO
 */
export interface LoginResponse {
  success: boolean;
  user?: {
    id: string;
    email: string;
    name: string;
    role: string;
    isActive: boolean;
    organization_id?: string;
    organization_name?: string;
    profileImageUrl?: string;
    lastLoginAt?: Date;
  };
  token?: {
    accessToken: string;
    refreshToken: string;
    expiresAt: Date;
    expiresIn: number; // seconds
  };
  session?: {
    id: string;
    expiresAt?: Date;
    lastAccessedAt?: Date;
  };
  error?: string;
  errorCode?: string;
  locked?: boolean;
  lockDuration?: number; // minutes
  needsVerification?: boolean;
  metadata?: {
    loginAttemptId?: string;
    riskScore?: number;
    recommendations?: string[];
  };
}

/**
 * 로그인 Use Case
 * 사용자 로그인 프로세스를 담당하는 애플리케이션 서비스
 */
export class LoginUseCase {
  private authService: AuthService;

  constructor() {
    const repository = AuthRepositoryFactory.getInstance();
    this.authService = new AuthService(repository);
  }

  /**
   * 로그인 실행
   */
  async execute(request: LoginRequest): Promise<LoginResponse> {
    try {
      // 1. 입력 유효성 검증
      const validationResult = this.validateRequest(request);
      if (!validationResult.isValid) {
        return {
          success: false,
          error: validationResult.error,
          errorCode: 'VALIDATION_FAILED',
        };
      }

      // 2. Email 값 객체 생성
      const email = Email.create(request.email);
      if (!email) {
        return {
          success: false,
          error: '올바른 이메일 형식이 아닙니다.',
          errorCode: 'INVALID_EMAIL',
        };
      }

      // 3. 로그인 옵션 구성
      const loginOptions: LoginOptions = {
        rememberMe: request.rememberMe || false,
        deviceXCircle: request.deviceXCircle ? {
          userAgent: request.deviceXCircle.userAgent,
          ipAddress: request.deviceXCircle.ipAddress,
        } : undefined,
      };

      // 4. 로그인 시도 전 사전 검증
      const preValidation = await this.preValidateLogin(email, request.deviceXCircle?.ipAddress);
      if (!preValidation.allowed) {
        return {
          success: false,
          error: preValidation.reason,
          errorCode: preValidation.errorCode,
          locked: preValidation.locked,
          lockDuration: preValidation.lockDuration,
        };
      }

      // 5. AuthService를 통한 로그인 수행
      const authResult = await this.authService.login(
        email,
        request.password,
        loginOptions
      );

      // 6. 결과를 Response DTO로 변환
      return this.mapToResponse(authResult, request);

    } catch (error) {
      console.error('로그인 Use Case 실행 중 오류:', error);
      return {
        success: false,
        error: '로그인 처리 중 시스템 오류가 발생했습니다.',
        errorCode: 'SYSTEM_ERROR',
      };
    }
  }

  /**
   * 요청 유효성 검증
   */
  private validateRequest(request: LoginRequest): {
    isValid: boolean;
    error?: string;
  } {
    if (!request.email || !request.password) {
      return {
        isValid: false,
        error: '이메일과 비밀번호를 입력해주세요.',
      };
    }

    if (request.email.length > 254) {
      return {
        isValid: false,
        error: '이메일 주소가 너무 깁니다.',
      };
    }

    if (request.password.length < 1) {
      return {
        isValid: false,
        error: '비밀번호를 입력해주세요.',
      };
    }

    if (request.password.length > 128) {
      return {
        isValid: false,
        error: '비밀번호가 너무 깁니다.',
      };
    }

    return { isValid: true };
  }

  /**
   * 로그인 사전 검증
   */
  private async preValidateLogin(
    email: Email,
    ipAddress?: string
  ): Promise<{
    allowed: boolean;
    reason?: string;
    errorCode?: string;
    locked?: boolean;
    lockDuration?: number;
  }> {
    try {
      // IP 기반 제한 검사 (예: 브루트 포스 공격 방지)
      if (ipAddress) {
        const ipValidation = await this.validateIPAddress(ipAddress);
        if (!ipValidation.allowed) {
          return {
            allowed: false,
            reason: ipValidation.reason,
            errorCode: 'IP_RESTRICTED',
          };
        }
      }

      // 시간 기반 제한 검사 (예: 운영 시간)
      const timeValidation = this.validateLoginTime();
      if (!timeValidation.allowed) {
        return {
          allowed: false,
          reason: timeValidation.reason,
          errorCode: 'TIME_RESTRICTED',
        };
      }

      return { allowed: true };

    } catch (error) {
      console.error('로그인 사전 검증 중 오류:', error);
      return {
        allowed: false,
        reason: '로그인 검증 중 오류가 발생했습니다.',
        errorCode: 'VALIDATION_ERROR',
      };
    }
  }

  /**
   * IP 주소 검증
   */
  private async validateIPAddress(ipAddress: string): Promise<{
    allowed: boolean;
    reason?: string;
  }> {
    // 여기서는 간단한 예시만 구현
    // 실제로는 IP 블랙리스트, 지역 제한, 비정상적인 패턴 등을 검사

    // localhost/개발 환경 허용
    if (ipAddress === '127.0.0.1' || ipAddress === '::1' || ipAddress === 'unknown') {
      return { allowed: true };
    }

    // 실제 구현에서는 데이터베이스에서 IP 블랙리스트 확인
    // 예: 최근 1시간 내 5회 이상 실패한 IP 차단
    // const repository = AuthRepositoryFactory.getInstance();
    // const recentFailures = await repository.findLoginAttemptsByIP(ipAddress, 50);
    // const failureCount = recentFailures.filter(attempt => 
    //   attempt.timestamp > new Date(Date.now() - 60 * 60 * 1000) && !attempt.success
    // ).length;
    // 
    // if (failureCount >= 5) {
    //   return {
    //     allowed: false,
    //     reason: 'IP 주소가 일시적으로 차단되었습니다. 1시간 후 다시 시도해주세요.',
    //   };
    // }

    return { allowed: true };
  }

  /**
   * 로그인 시간 검증
   */
  private validateLoginTime(): {
    allowed: boolean;
    reason?: string;
  } {
    // 24시간 운영으로 가정하고 항상 허용
    // 실제 구현에서는 운영 시간 설정에 따라 제한
    
    const now = new Date();
    const hour = now.getHours();
    
    // 예: 새벽 2-4시 시스템 점검 시간 (주석 처리됨)
    // if (hour >= 2 && hour < 4) {
    //   return {
    //     allowed: false,
    //     reason: '시스템 점검 시간(02:00-04:00)입니다. 점검 완료 후 다시 시도해주세요.',
    //   };
    // }

    return { allowed: true };
  }

  /**
   * AuthenticationResult를 LoginResponse로 변환
   */
  private mapToResponse(
    authResult: AuthenticationResult,
    request: LoginRequest
  ): LoginResponse {
    if (!authResult.success) {
      return {
        success: false,
        error: authResult.error,
        errorCode: this.getErrorCode(authResult.error),
        locked: authResult.locked,
        needsVerification: authResult.needsVerification,
      };
    }

    // 성공한 경우
    const response: LoginResponse = {
      success: true,
    };

    // 사용자 정보 매핑
    if (authResult.user) {
      response.user = {
        id: authResult.user.id.value,
        email: authResult.user.email.value,
        name: authResult.user.name,
        role: authResult.user.role.value,
        isActive: authResult.user.isActive,
        organization_id: authResult.user.organizationId,
        organization_name: authResult.user.organizationName,
        profileImageUrl: authResult.user.profileImageUrl,
        lastLoginAt: authResult.user.lastLoginAt,
      };
    }

    // 토큰 정보 매핑
    if (authResult.token) {
      const expiresIn = Math.floor(
        (authResult.token.expiresAt.getTime() - Date.now()) / 1000
      );
      
      response.token = {
        accessToken: authResult.token.accessToken,
        refreshToken: authResult.token.refreshToken,
        expiresAt: authResult.token.expiresAt,
        expiresIn: Math.max(0, expiresIn),
      };
    }

    // 세션 정보 매핑
    if (authResult.session) {
      response.session = {
        id: authResult.session.id.value,
        expiresAt: authResult.session.expiresAt,
        lastAccessedAt: authResult.session.lastAccessedAt,
      };
    }

    // 메타데이터 추가
    response.metadata = {
      loginAttemptId: `login_${Date.now()}`,
      riskScore: this.calculateRiskScore(request),
      recommendations: this.getSecurityRecommendations(authResult.user, request),
    };

    return response;
  }

  /**
   * 오류 메시지를 기반으로 오류 코드 생성
   */
  private getErrorCode(error?: string): string {
    if (!error) return 'UNKNOWN_ERROR';

    if (error.includes('이메일') || error.includes('비밀번호')) {
      return 'INVALID_CREDENTIALS';
    }
    if (error.includes('잠겨')) {
      return 'ACCOUNT_LOCKED';
    }
    if (error.includes('비활성화')) {
      return 'ACCOUNT_INACTIVE';
    }
    if (error.includes('실패')) {
      return 'LOGIN_FAILED';
    }

    return 'LOGIN_ERROR';
  }

  /**
   * 로그인 위험도 점수 계산
   */
  private calculateRiskScore(request: LoginRequest): number {
    let score = 0;

    // 기본 점수
    score = 10;

    // 디바이스 정보가 없으면 위험도 증가
    if (!request.deviceXCircle) {
      score += 20;
    }

    // 시간대별 위험도
    const hour = new Date().getHours();
    if (hour >= 22 || hour <= 6) {
      score += 15; // 심야 시간대
    }

    // Remember Me 사용시 위험도 증가
    if (request.rememberMe) {
      score += 10;
    }

    return Math.min(100, score);
  }

  /**
   * 보안 권장사항 생성
   */
  private getSecurityRecommendations(
    user?: User,
    request?: LoginRequest
  ): string[] {
    const recommendations: string[] = [];

    // 비밀번호 변경 권장
    if (user?.passwordChangedAt) {
      const daysSincePasswordChange = Math.floor(
        (Date.now() - user.passwordChangedAt.getTime()) / (1000 * 60 * 60 * 24)
      );
      
      if (daysSincePasswordChange > 90) {
        recommendations.push('비밀번호를 90일 이상 사용하고 있습니다. 보안을 위해 변경을 권장합니다.');
      }
    }

    // 2FA 권장
    // if (!user?.twoFactorEnabled) {
    //   recommendations.push('계정 보안 강화를 위해 2단계 인증 설정을 권장합니다.');
    // }

    // 디바이스 정보 미제공시
    if (!request?.deviceXCircle) {
      recommendations.push('보안을 위해 신뢰할 수 있는 디바이스에서만 로그인하세요.');
    }

    return recommendations;
  }

  /**
   * 로그인 상태 확인 (현재 세션 유효성)
   */
  async checkLoginStatus(): Promise<{
    isLoggedIn: boolean;
    user?: LoginResponse['user'];
    needsRefresh?: boolean;
    needsLogin?: boolean;
  }> {
    try {
      const validation = await this.authService.validateAndRefreshSession();
      
      if (!validation.isValid) {
        return {
          isLoggedIn: false,
          needsLogin: validation.needsLogin || false,
        };
      }

      // 사용자 정보 매핑
      let userXCircle: LoginResponse['user'] | undefined;
      if (validation.user) {
        userXCircle = {
          id: validation.user.id?.value || '',
          email: validation.user.email?.value || '',
          name: validation.user.name || '',
          role: validation.user.role?.value || '',
          isActive: validation.user.isActive || false,
          profileImageUrl: validation.user.profileImageUrl,
          lastLoginAt: validation.user.lastLoginAt,
        };
      }

      return {
        isLoggedIn: true,
        user: userXCircle,
      };

    } catch (error) {
      console.error('로그인 상태 확인 중 오류:', error);
      return {
        isLoggedIn: false,
        needsLogin: true,
      };
    }
  }
}